import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, ArrowRight, Stethoscope, Shield, MessageCircle, UserPlus, CheckCircle } from 'lucide-react';
import Breadcrumb from '@/components/Breadcrumb';
import JumpToNav from '@/components/JumpToNav';
import { SPECIALIZATIONS } from '@/data/platformSeoData';

// ============================================================================
// ATLAS TAGLINES — one infrastructure-forward line per specialty.
// No head-counts. No verification claims. Focus: "we built content for YOUR field."
// ============================================================================
const ATLAS_TAGLINES = {
  cardiology: 'Echo, Cath Lab, Angioplasty — each gets its own page.',
  dermatology: 'Before/after galleries, PRP, chemical peels, laser protocols.',
  ent: 'FESS, septoplasty, tonsillectomy, cochlear-implant templates.',
  gastroenterology: 'Endoscopy, colonoscopy, piles, liver-care scaffolds.',
  physician: 'Multi-system care: HTN, diabetes, thyroid, anaemia, post-discharge continuity — the orchestrator surface for MD Medicine consultants.',
  general_surgery: 'Laparoscopic, hernia, appendix, day-care protocol pages.',
  gynecology: 'Obstetrics, IUD, colposcopy, hysteroscopy — consent-first design.',
  neurology: 'Epilepsy, Parkinson\'s, migraine, stroke rehab — each bookmark-able.',
  neurosurgery: 'Spine, tumour, vascular, functional sub-specialty pages.',
  oncology: 'Medical, surgical, radiation onc sub-specialty content scaffolds.',
  ophthalmology: 'Cataract, LASIK, retina, glaucoma, squint — each ranks alone.',
  orthopedics: 'TKR, THR, arthroscopy, spine — procedure-level ranking pages.',
  pediatrics: 'Vaccination schedules, growth tracking, specialty-clinic content.',
  psychiatry: 'Calm design for anxiety, depression, OCD, ADHD conditions.',
  pulmonology: 'Asthma, COPD, sleep apnoea, post-COVID rehab pages.',
  urology: 'PCNL, URS, TURP, andrology — confidential booking paths.',
  dentistry: 'Root-canal, implant, aligner, whitening — with before/after stories.',
  endocrinology: 'Diabetes, thyroid, PCOS, pituitary follow-up infrastructure.',
  nephrology: 'CKD continuity, dialysis, transplant follow-up pages.',
  rheumatology: 'RA, lupus, AS, gout — condition-first content scaffolds.',
  plastic_surgery: 'Procedure portfolios with consent-first patient-story design.',
  allergy_immunology: 'Skin-prick, immunotherapy, food-allergy, anaphylaxis pages.',
  radiology: 'Neuro, MSK, abdominal, IR — referrer-focused page templates.',
  anesthesiology: 'Pre-op, regional, obstetric, pain-clinic service pages.',
  physiotherapy: 'Back pain, post-TKR, stroke, sports-injury rehab pages.',
  diabetology: 'CGM, insulin pumps, diabetic foot, gestational infrastructure.',
  infectious_disease: 'HIV, TB, hepatitis, travel-medicine referrer content.',
  vascular_surgery: 'Varicose, EVAR, carotid, AV-fistula, limb-salvage pages.',
  pain_management: 'Epidural, RFA, SCS, cancer-pain, neuropathic content.',
};

// Mirror of the server-side palette — for the 7px accent dot only.
const SPECIALTY_PALETTE = {
  cardiology: '#DC2626', dermatology: '#DB2777', ent: '#7C3AED', gastroenterology: '#EA580C',
  physician: '#0284C7',                 // MD Medicine
  general_medicine: '#0284C7',          // legacy alias → physician
  general_surgery: '#0891B2', gynecology: '#BE185D', neurology: '#6D28D9',
  neurosurgery: '#4338CA', oncology: '#0F766E', ophthalmology: '#0369A1', orthopedics: '#1D4ED8',
  pediatrics: '#059669', psychiatry: '#7E22CE', pulmonology: '#0D9488', urology: '#B45309',
  dentistry: '#0E7490', endocrinology: '#B91C1C', nephrology: '#1E40AF', rheumatology: '#A21CAF',
  plastic_surgery: '#C026D3', allergy_immunology: '#15803D', radiology: '#525252', anesthesiology: '#475569',
  internal_medicine: '#0284C7',         // legacy alias → physician
  physiotherapy: '#16A34A', diabetology: '#D97706',
  infectious_disease: '#CA8A04', vascular_surgery: '#B91C1C', pain_management: '#9333EA',
};

// FAQ content (rendered + Schema.org FAQPage).
const FAQS = [
  {
    q: 'Does DocSite verify doctor credentials?',
    a: 'No. Each doctor publishes under a signed, truthful-declaration consent. Every published change is audit-logged and immutable. The doctor is solely responsible for accuracy — DocSite is not. This model mirrors LinkedIn and Substack; it is not an aggregator directory that claims verification.',
  },
  {
    q: 'Why doesn\'t DocSite show a "doctors listed" count?',
    a: 'Because you\'re not a listing. DocSite is a product platform — not a directory and not a website builder. Your DocSite practice surface lives at your own URL, with your own content and your own patient conversations. You are not a row on a competitor\'s table; you are the specialist patients reach first.',
  },
  {
    q: 'What if my specialty isn\'t listed here?',
    a: 'Write to contact@docsite.care with the specialty name. We add new specialties in 24 hours. The platform itself supports any medical field — the listed 30 are simply where hand-authored copy is already published.',
  },
  {
    q: 'How is DocSite different from a directory or an aggregator listing?',
    a: 'A directory keeps the URL, the SEO juice, and the patient data — and many of them also charge commission. DocSite is a platform that returns all of that to the specialist: your own slug, your own audit-logged content as a career asset, and zero commission on bookings. The conversation between doctor and patient stays specialist-led, not mediated by a directory or a hospital marketing team.',
  },
];

export default function ForDoctorsHub() {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SPECIALIZATIONS;
    return SPECIALIZATIONS.filter(s =>
      s.name.toLowerCase().includes(q) ||
      s.slug.toLowerCase().includes(q) ||
      (ATLAS_TAGLINES[s.slug] || '').toLowerCase().includes(q)
    );
  }, [query]);

  // Schema.org: ItemList of all specialty pages
  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'DocSite — Medical websites built for every specialty',
    description: 'Hand-authored infrastructure for 30 medical specialties. One website per doctor — not a directory listing.',
    itemListOrder: 'https://schema.org/ItemListOrderAscending',
    numberOfItems: SPECIALIZATIONS.length,
    itemListElement: SPECIALIZATIONS.map((s, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: s.name,
      url: `https://docsite.care/for/${s.slug}`,
      description: ATLAS_TAGLINES[s.slug] || `Website template tuned for ${s.name}.`,
    })),
  };

  // Schema.org: FAQPage
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="for-doctors-hub">
      <Helmet>
        <title>Medical websites built for every specialty — DocSite</title>
        <meta name="description" content="30 specialties. Hand-authored content scaffolds for each field. One website, your URL, your audience. 0% commission, audit-logged edits, AES-128 field encryption." />
        <link rel="canonical" href="https://docsite.care/for" />
        <meta property="og:title" content="Medical websites built for every specialty — DocSite" />
        <meta property="og:description" content="A website of your own — tuned for your field. 30 specialties, 0% commission, your content, your URL." />
        <meta property="og:url" content="https://docsite.care/for" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(itemListJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28 pt-12 md:pt-16">
        <Breadcrumb items={[
          { label: 'DocSite', to: '/' },
          { label: 'For Doctors' },
        ]} />
      </div>

      {/* Masthead — two-column: editorial heading left, live signal + value-prop card right */}
      <header className="w-full px-6 md:px-12 lg:px-20 xl:px-28 pt-12 pb-2 md:pt-16 md:pb-4" data-testid="atlas-masthead">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-7 xl:col-span-7">
            <h1 className="font-medium tracking-tight text-slate-900 leading-[1.05]" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(2.5rem, 4.4vw, 4.75rem)' }}>
              A platform of your own —<br />
              <span className="text-slate-500">tuned for your field.</span>
            </h1>
            <p className="mt-7 text-slate-600 leading-relaxed lg:max-w-[60ch]" style={{ fontSize: 'clamp(1.05rem, 1.15vw, 1.3rem)' }}>
              DocSite is a product platform that brings the doctor–patient conversation back to the specialist. 30 medical specialties, 40+ per-procedure page templates, your slug, your audit-logged content. Not a website builder. Not a directory. The infrastructure layer for specialist-led care.
            </p>

            <div className="mt-7 pt-5 border-t border-slate-200 space-y-4">
              <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-slate-600 font-medium" style={{ fontSize: 'clamp(0.95rem, 1.05vw, 1.2rem)' }} data-testid="for-doctors-trust-row">
                <span className="flex items-center gap-2"><CheckCircle size={20} className="text-emerald-500" /> Free Gold tier</span>
                <span className="flex items-center gap-2"><CheckCircle size={20} className="text-emerald-500" /> 30+ specialties</span>
                <span className="flex items-center gap-2"><CheckCircle size={20} className="text-emerald-500" /> 5-minute setup</span>
              </div>

              <JumpToNav
                testId="for-doctors-jump-nav"
                className="!mt-0 !pt-0 !border-0"
                items={[
                  { icon: Stethoscope, label: 'Browse specialties', href: '#specialties', color: 'sky' },
                  { icon: Shield, label: 'What you sign', href: '#trust-band', color: 'slate' },
                  { icon: MessageCircle, label: 'FAQs', href: '#faqs', color: 'violet' },
                  { icon: UserPlus, label: 'Get Started, FREE', to: '/register?as=doctor&from=for_doctors_hub', color: 'emerald' },
                ]}
              />
            </div>
          </div>

          {/* Right rail — specialty-mosaic preview that previews the per-specialty depth */}
          <aside className="lg:col-span-5 xl:col-span-5" data-testid="for-doctors-rail">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="bg-slate-700 px-4 py-2.5 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <span className="text-slate-300 ml-2 text-[11px] font-mono">docsite.care/for/{'{your-specialty}'}</span>
              </div>
              <div className="p-5">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">A glimpse of what each specialty page covers</p>
                <div className="grid grid-cols-2 gap-2">
                  {SPECIALIZATIONS.slice(0, 8).map(s => (
                    <div key={s.slug} className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-slate-50 border border-slate-100">
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: SPECIALTY_PALETTE[s.slug] || '#0284C7' }} />
                      <span className="text-[11px] text-slate-700 font-medium truncate">{s.name}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 mt-3">+ 22 more specialties below — each with hand-authored copy, FAQ schema, and 40+ per-procedure templates.</p>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-sky-50/60 via-white to-emerald-50/40 p-6 mt-5">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-700 mb-3">Why a platform, not a website builder</p>
              <ul className="space-y-3 text-sm text-slate-700">
                <li className="flex items-start gap-2.5">
                  <Stethoscope size={14} className="text-sky-600 flex-shrink-0 mt-1" />
                  <span>
                    <strong className="text-slate-900">You're the patient's first call.</strong>{' '}
                    Not a tile inside an aggregator, not the doctor named on a hospital's landing page — the specialist patients actually reach.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Shield size={14} className="text-emerald-600 flex-shrink-0 mt-1" />
                  <span>
                    <strong className="text-slate-900">Patient data stays locked.</strong>{' '}
                    Phone numbers and emails are encrypted at rest with bank-grade security — even a database leak couldn't expose them.{' '}
                    <Link to="/security/patient-data" className="text-emerald-700 hover:text-emerald-800 underline decoration-dotted underline-offset-2" data-testid="patient-data-link-from-hub">
                      See the one-pager →
                    </Link>
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <MessageCircle size={14} className="text-violet-600 flex-shrink-0 mt-1" />
                  <span>
                    <strong className="text-slate-900">Zero commission on bookings.</strong>{' '}
                    Every appointment lands on your phone, every fee stays in your pocket. No revenue share, ever.
                  </span>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </header>

      {/* Search — bold, present, visible. Sits right under the masthead with no dead zone. */}
      <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28 pt-2 pb-10 md:pb-14" data-testid="atlas-search-wrap">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-700 mb-3 inline-flex items-center gap-1.5">
              <Stethoscope size={12} /> Browse all 30 specialties
            </p>
            <div className="relative" data-testid="atlas-search-shell">
              <Search size={22} className="absolute left-5 top-1/2 -translate-y-1/2 text-sky-600 pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search your specialty — Cardiology, ENT, Ortho, Physician (MD Medicine)…"
                className="w-full pl-14 pr-5 py-5 bg-white border-2 border-sky-200 rounded-2xl text-slate-900 placeholder-slate-400 shadow-sm hover:border-sky-300 focus:outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-100 transition-all"
                style={{ fontSize: 'clamp(1rem, 1.15vw, 1.2rem)' }}
                data-testid="atlas-search"
              />
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Don't see your specialty? <a href="mailto:contact@docsite.care?subject=Please%20add%20my%20specialty" className="text-sky-700 hover:text-sky-800 underline decoration-dotted underline-offset-2 font-medium">Write to us</a> — we add new ones in 24 hours.
            </p>
          </div>
        </div>
      </div>

      {/* Editorial list — one line per specialty */}
      <section id="specialties" className="w-full px-6 md:px-12 lg:px-20 xl:px-28 pb-24 scroll-mt-24" aria-label="Specialties">
        <ol className="divide-y divide-slate-100" data-testid="atlas-specialty-list">
          {filtered.map(s => {
            const tagline = ATLAS_TAGLINES[s.slug] || `Website template tuned for ${s.name}.`;
            const color = SPECIALTY_PALETTE[s.slug] || '#0284C7';
            return (
              <li key={s.slug} data-testid={`atlas-row-${s.slug}`}>
                <Link
                  to={`/for/${s.slug}`}
                  className="group flex items-center gap-4 md:gap-6 py-6 md:py-7 hover:bg-slate-50/50 transition-colors -mx-3 md:-mx-4 px-3 md:px-4 rounded-md"
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0 opacity-80 group-hover:opacity-100 transition-opacity"
                    style={{ backgroundColor: color }}
                    aria-hidden="true"
                  />
                  <div className="flex-1 min-w-0 grid md:grid-cols-[260px_1fr] gap-2 md:gap-10 items-baseline">
                    <h2 className="font-medium text-slate-900 group-hover:text-sky-700 transition-colors tracking-tight leading-tight" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.25rem, 1.6vw, 1.65rem)' }}>
                      {s.name}
                    </h2>
                    <p className="text-slate-600 leading-relaxed" style={{ fontSize: 'clamp(0.95rem, 1.05vw, 1.15rem)' }}>{tagline}</p>
                  </div>
                  <ArrowRight size={20} className="text-slate-300 group-hover:text-sky-600 group-hover:translate-x-0.5 transition-all flex-shrink-0 hidden md:block" />
                </Link>
              </li>
            );
          })}
        </ol>

        {filtered.length === 0 && (
          <div className="py-12 text-center text-sm text-slate-500" data-testid="atlas-empty">
            No specialty matches "{query}". <a href="mailto:contact@docsite.care?subject=Please%20add%20my%20specialty" className="text-sky-600 hover:text-sky-700 font-medium">Write to us</a> and we'll add it in 24 hours.
          </div>
        )}
      </section>

      {/* Trust band — prose, no badges, no stars */}
      <section id="trust-band" className="bg-slate-50/70 border-t border-slate-100 py-20 md:py-28 scroll-mt-24" data-testid="atlas-trust-band">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold mb-6">What you get · What you sign · What we do</p>
          <div className="space-y-5 text-[15px] leading-relaxed text-slate-700">
            <p><span className="font-medium text-slate-900">You publish under a signed, truthful declaration.</span> You control every word. If a claim is false, you — not DocSite — are answerable.</p>
            <p><span className="font-medium text-slate-900">Every edit is audit-logged and immutable.</span> The record of what you've published, when, stays intact. That's your accountability and your defense.</p>
            <p><span className="font-medium text-slate-900">Your email, phone, and registration number are encrypted at rest</span> — AES-128, field-level. Even a database breach can't read them.</p>
            <p><span className="font-medium text-slate-900">Zero commission on patient bookings. Ever.</span> No share of your consultation, no middleman between you and your patient.</p>
            <p><span className="font-medium text-slate-900">You own your URL, your content, and your patient relationships.</span> Leave anytime. Export everything.</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faqs" className="py-20 md:py-28 scroll-mt-24" data-testid="atlas-faq">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold mb-8">Frequently asked</p>
          <div className="space-y-10">
            {FAQS.map((f, i) => (
              <div key={i}>
                <h3 className="text-lg md:text-xl font-medium text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.q}</h3>
                <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing — single line */}
      <section className="border-t border-slate-100 py-14 text-center">
        <p className="text-sm text-slate-500">
          Don't see your specialty?{' '}
          <a href="mailto:contact@docsite.care?subject=Please%20add%20my%20specialty" className="text-sky-600 hover:text-sky-700 font-medium underline" data-testid="atlas-add-specialty-link">
            Tell us
          </a>{' '}— we'll add it in 24 hours.
        </p>
      </section>
    </div>
  );
}
